import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';

/**
 * Determines whether the onboarding form should be shown.
 * Shows for account owners who haven't completed or skipped the onboarding form.
 * Skips for white-label accounts and embedded contexts.
 *
 * Reads onboarding state from me.userSettings (already fetched by ME_QUERY in AuthProvider)
 * — no separate query needed.
 */

/**
 * Only show the onboarding form for accounts created after this date.
 * Existing accounts before feature launch should never see the questionnaire.
 * Update this date to match the production deployment date of this feature.
 */
const ONBOARDING_LAUNCH_DATE = new Date('2026-03-16T00:00:00Z');

/** Set VITE_FORCE_ONBOARDING=true in sandbox to bypass the date gate for testing. */
const FORCE_ONBOARDING = import.meta.env.VITE_FORCE_ONBOARDING === 'true';

/** Set VITE_ENABLE_ONBOARDING=false to disable the onboarding form entirely.
 *  New users will have onboardFormSubmitted auto-set to true. */
const ENABLE_ONBOARDING = import.meta.env.VITE_ENABLE_ONBOARDING !== 'false';

export function useShouldShowOnboarding(): boolean {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const pendingOnboarding = useAuthStore((s) => s.pendingOnboarding);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const [updateSettings] = useMutation(UPDATE_USER_SETTINGS_AUTH);

  // Auto-submit ref: ensures we only fire the mutation once per mount
  const autoSubmittedRef = useRef(false);

  // me.userSettings is Record<string, unknown> from ME_QUERY — narrow with safe property access
  const rawSettings = me?.userSettings;
  const userSettings = rawSettings
    ? {
        onboardFormSubmitted:
          'onboardFormSubmitted' in rawSettings &&
          typeof rawSettings.onboardFormSubmitted === 'boolean'
            ? rawSettings.onboardFormSubmitted
            : undefined,
        onboardFormSkip:
          'onboardFormSkip' in rawSettings && typeof rawSettings.onboardFormSkip === 'boolean'
            ? rawSettings.onboardFormSkip
            : undefined,
      }
    : null;

  // Determine if this user would normally see the onboarding form
  const wouldShowForm =
    !!me &&
    !!company &&
    !!userSettings &&
    !isEmbedded &&
    me.rootRole === ROLE_TYPES.OWNER &&
    me.brandingData?.isWhiteLabel !== true &&
    (FORCE_ONBOARDING || new Date(company.createdAt) >= ONBOARDING_LAUNCH_DATE) &&
    !userSettings.onboardFormSubmitted &&
    !userSettings.onboardFormSkip;

  // When onboarding is disabled, auto-mark as submitted so the form never appears.
  // Handles social signup path (arrives via AppShell) and any login where settings aren't yet marked.
  useEffect(() => {
    if (!ENABLE_ONBOARDING && wouldShowForm && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      useAuthStore.getState().setPendingOnboarding(false);
      // Patch auth-store immediately so this hook returns false on re-render
      const currentMe = useAuthStore.getState().me;
      if (currentMe) {
        useAuthStore.getState().setMe({
          ...currentMe,
          userSettings: { ...currentMe.userSettings, onboardFormSubmitted: true },
        });
      }
      // Persist to backend (best-effort, no refetch needed)
      updateSettings({
        variables: { updateData: { onboardFormSubmitted: true }, teamId: activeTeamId },
      });
    }
  }, [wouldShowForm, updateSettings, activeTeamId]);

  // Global kill switch — never show for anyone when env-disabled.
  if (!ENABLE_ONBOARDING) {
    return false;
  }

  // SignupPage verified onboarding should show and set this flag before navigating.
  // Return true immediately so AppShell renders OnboardingForm on the first frame,
  // with zero gap between the signup page and the questionnaire.
  if (pendingOnboarding) {
    return true;
  }

  // Need me and company data to decide — until then, don't show.
  if (!me || !company) {
    return false;
  }

  // Existing accounts created before the feature launch (bypass with VITE_FORCE_ONBOARDING)
  if (!FORCE_ONBOARDING && new Date(company.createdAt) < ONBOARDING_LAUNCH_DATE) {
    return false;
  }

  // Quick exit for non-candidates
  if (isEmbedded || me.rootRole !== ROLE_TYPES.OWNER || me.brandingData?.isWhiteLabel === true) {
    return false;
  }

  // No settings record = existing user who never triggered settings creation.
  // Don't show the onboarding form — it's only for brand-new signups.
  if (!userSettings) {
    return false;
  }

  return !userSettings.onboardFormSubmitted && !userSettings.onboardFormSkip;
}
